/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { BuilderPermission, BuilderPermissionSet, ContactType } from '@gh/builder-shared-data';
import { UserContext } from '@gh/common-shared';
import { Permission } from '@gh/core-auth';
import { Enum } from '@gh/core-data';
import { STYLES_FLEX_CROSS_CENTER, STYLES_FLEX_ROW_LAYOUT } from '@gh/core-ui';
import { coerceStringArrayProperty } from '@gh/core-util';
import { difference, intersection, isString, property, uniq } from 'lodash';

function getContactCreatePermission(type: ContactType): Permission {
  return {
    [ContactType.CreditorSupplier]: BuilderPermission.Contacts.CreditorSupplier.CREATE_EDIT,
    [ContactType.Debtor]: BuilderPermission.Contacts.Debtor.CREATE_EDIT,
    [ContactType.Customer]: BuilderPermission.Contacts.Customer.CREATE_EDIT,
    [ContactType.Employee]: BuilderPermission.Contacts.Employee.CREATE_EDIT,
    [ContactType.FinanceCompany]: BuilderPermission.Contacts.FinanceCompany.CREATE_EDIT,
    [ContactType.CityCounty]: BuilderPermission.Contacts.CityCounty.CREATE_EDIT,
    [ContactType.Other]: BuilderPermission.Contacts.Other.CREATE_EDIT,
  }[type];
}

function getContactOpenPermission(type: ContactType): Permission {
  return {
    [ContactType.CreditorSupplier]: BuilderPermissionSet.Contacts.CreditorSupplier.ANY,
    [ContactType.Debtor]: BuilderPermissionSet.Contacts.Debtor.ANY,
    [ContactType.Customer]: BuilderPermissionSet.Contacts.Customer.ANY,
    [ContactType.Employee]: BuilderPermissionSet.Contacts.Employee.ANY,
    [ContactType.FinanceCompany]: BuilderPermissionSet.Contacts.FinanceCompany.ANY,
    [ContactType.CityCounty]: BuilderPermissionSet.Contacts.CityCounty.ANY,
    [ContactType.Other]: BuilderPermissionSet.Contacts.Other.ANY,
  }[type];
}

@Component({
  standalone: false,
  selector: 'gh-contact-menu',
  templateUrl: 'contact-menu.component.html',
  styles: [STYLES_FLEX_ROW_LAYOUT, STYLES_FLEX_CROSS_CENTER],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactMenuComponent implements OnInit {
  @Input() disabled: boolean = false;
  @Input() visibleIfDisabled = false;
  @Input() value?: any;

  @Output() create = new EventEmitter<void>();
  @Output() open = new EventEmitter<any>();

  private createPermissionsFor: ContactType[];
  private openPermissionsFor: ContactType[];
  private _types: ContactType[] = [];
  private _typeWith?: TransformFn<any, ContactType[]>;

  @Input()
  set typeWith(typeWith: TransformFn<any, ContactType[]> | string) {
    this._typeWith = isString(typeWith) ? property(typeWith) : typeWith;
  }

  @Input()
  set type(types: string | string[]) {
    this._types = <ContactType[]>uniq(coerceStringArrayProperty(types));

    const unknownTypes = difference(this._types, Enum.names(ContactType));
    if (unknownTypes.length > 0) {
      throw new Error(`ContactMenuComponent: the following types are unknown: ${unknownTypes.join(',')}`);
    }
  }

  get types(): ContactType[] {
    return this._types;
  }

  get editable(): boolean {
    return !this.disabled;
  }

  get canCreate(): boolean {
    return this.editable && this.hasCreatePermission;
  }

  get canOpen(): boolean {
    return (this.editable || this.visibleIfDisabled) && this.isAssigned && this.hasOpenPermission;
  }

  /**
   * Determines if contact of allowed contact type is assigned
   */
  get isAssigned(): boolean {
    if (this.value) {
      // if typesWith is defined we try to find intersection of allowed contact types
      // and types returned by typesWith function
      return this._typeWith ? intersection(this._typeWith(this.value), this.openPermissionsFor).length > 0 : true;
    } else {
      return false;
    }
  }

  get hasCreatePermission(): boolean {
    return this.createPermissionsFor.length > 0;
  }

  get hasOpenPermission(): boolean {
    return this.openPermissionsFor.length > 0;
  }

  constructor(private changeDetectorRef: ChangeDetectorRef,
              private userContext: UserContext) {

  }

  ngOnInit(): void {
    this.createPermissionsFor = this._types.filter((type) =>
      this.userContext.hasPermission(getContactCreatePermission(type)));
    this.openPermissionsFor = this._types.filter((type) =>
      this.userContext.hasPermission(getContactOpenPermission(type)));
  }

  onCreate(): void {
    this.create.emit();
  }

  onOpen(): void {
    this.open.emit(this.value);
  }

  _markForCheck(): void {
    this.changeDetectorRef.markForCheck();
  }
}
